import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

// CONSTANTS
import {
  ROUTE_404,
  ROUTE_NO_APP_FOUND,
  ROUTE_DISCOVER,
  ROUTE_DISCOVER_APP,
} from '@/constants/routes';
import { LOCATIONS_SUBSCIPTION_PAGE } from '@/constants/locations';
import { SubscriptionFlow } from '@/constants/subscription';

// UTILITY
import store from '@/utility/store';
import { getSearchParams } from '@/utility/common';
import { checkIsAbsoluteUrl, getLink, parseStingToArray, routeEnhance } from '@/utility/routes';
import { analyticsPageLoaded } from '@/utility/analytics';

// STORE
import { SubscriptionActions, AuthActions } from '@/store/actions';

// COMPONENTS
import Loader from '@/components/Loader';
import Redirect from '@/components/Redirect';

// HELP
import SubscriptionForm from './help/SubscriptionForm';

function getDictionaryData(slug, searchParams) {
  const subscrData = store.get('subscription') || null;
  const planIdParam = searchParams.get('plan_id');
  const addonsParam =
    searchParams.get('addons') && parseStingToArray(searchParams.get('addons'), ',');
  let planIdStored = '';
  let addonsStored = [];

  if (subscrData) {
    if (slug !== subscrData.merchant_slug) {
      store.remove('subscription');
      store.remove('sub-spec-offer');
    } else {
      planIdStored = subscrData.plan_id;
      addonsStored = subscrData.addons;
    }
  }

  return {
    plan_id: +planIdParam || +planIdStored || '',
    addons: addonsParam || addonsStored || [],
    slug,
  };
}

export const Subscription = ({
  isAuth,
  dictionaryLoading,
  paymentLoading,
  registrationLoading,
  cardsLoading,
  previewLoading,
  dictionary,
  preview,
  getSubscriptionDictionary,
  addSubscriptionRegistration,
  clearSubscriptionInfo,
  cards,
  errors,
  user,
  partner,
  partnerSettings,
  manageAuthDialogState,
}) => {
  const [t] = useTranslation();
  const { slug } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const params = getSearchParams(search);

  function getSubscriptionInfo() {
    const dictionaryData = getDictionaryData(slug, params);

    getSubscriptionDictionary({
      ...dictionaryData,
      onError: () => {
        navigate(routeEnhance(ROUTE_NO_APP_FOUND), { replace: true });
      },
    });

    analyticsPageLoaded(LOCATIONS_SUBSCIPTION_PAGE);
  }

  useEffect(() => {
    if (slug) getSubscriptionInfo();

    return clearSubscriptionInfo;
  }, [slug]);

  useEffect(() => {
    const subscrFlow = dictionary?.subscription?.registration_meta?.flow;
    const redirectUrl = dictionary?.subscription?.registration_meta?.redirect_url;
    const flowsWithAuthDialog = [
      SubscriptionFlow.DEFERRED_REGISTRATION,
      SubscriptionFlow.SINGLE_CHARGE_PROMOCODE,
    ];
    const isCorrectFlow = flowsWithAuthDialog.includes(subscrFlow);

    function onDialogClose() {
      const isExternalLink = redirectUrl && checkIsAbsoluteUrl(redirectUrl);
      let routeEnhanced = routeEnhance(getLink(ROUTE_DISCOVER_APP, { slug }));

      if (subscrFlow === SubscriptionFlow.SINGLE_CHARGE_PROMOCOD) {
        routeEnhanced = routeEnhance(ROUTE_DISCOVER);
      }

      if (redirectUrl && !isExternalLink) {
        routeEnhanced = routeEnhance(redirectUrl);
      }

      if (isExternalLink) {
        window.location.href = redirectUrl;
      } else {
        navigate(routeEnhanced, { replace: true });
      }
    }

    if (!isAuth && isCorrectFlow) {
      const isRokuDevice = slug === 'roku_streaming_device';
      manageAuthDialogState({
        screen: 'registration',
        title: isRokuDevice ? 'Confirm Your Email to Complete Purchase' : undefined,
        description: isRokuDevice
          ? 'We’ll send your receipt to this email and save your purchase to your MyBundle account for easy access. Just create a password to come back to it anytime. Plus, your free account unlocks personalized streaming recommendations.'
          : undefined,
        skip_app_onboarding: true,
        profDrawerState: false,
        callback: getSubscriptionInfo,
        onDialogClose,
      });
    }
  }, [isAuth, dictionary?.subscription]);

  if (
    !slug ||
    (dictionary && !dictionary.subscription) ||
    (partner.slug && !partnerSettings.use_subscription)
  ) {
    return <Redirect to={ROUTE_404} />;
  }

  const loading = dictionaryLoading || paymentLoading || registrationLoading || cardsLoading;
  const showForm = Boolean(dictionary?.subscription);

  return (
    <>
      {showForm && (
        <SubscriptionForm
          partner={partner}
          user={user}
          dictionary={dictionary}
          preview={preview}
          cards={cards}
          errors={errors}
          loading={loading}
          cardsLoading={cardsLoading}
          paymentLoading={paymentLoading}
          previewLoading={previewLoading}
          addSubscriptionRegistration={addSubscriptionRegistration}
          manageAuthDialogState={manageAuthDialogState}
        />
      )}
      <Loader
        active={loading}
        position="fixed"
        text={t(
          `notification.waitWhileSubscription${paymentLoading ? 'Activating' : 'Processing'}`,
        )}
        typographyProps={{ align: 'center' }}
      />
    </>
  );
};

Subscription.propTypes = {
  dictionaryLoading: PropTypes.bool.isRequired,
  paymentLoading: PropTypes.bool.isRequired,
  registrationLoading: PropTypes.bool.isRequired,
  cardsLoading: PropTypes.bool.isRequired,
  previewLoading: PropTypes.bool.isRequired,
  isAuth: PropTypes.bool.isRequired,
  dictionary: PropTypes.object,
  preview: PropTypes.object,
  user: PropTypes.object,
  cards: PropTypes.array.isRequired,
  errors: PropTypes.object.isRequired,
  getSubscriptionDictionary: PropTypes.func.isRequired,
  addSubscriptionRegistration: PropTypes.func.isRequired,
  clearSubscriptionInfo: PropTypes.func.isRequired,
  partner: PropTypes.object.isRequired,
  partnerSettings: PropTypes.object.isRequired,
  manageAuthDialogState: PropTypes.func.isRequired,
};

const mapStateToProps = ({
  user: userStore,
  partner,
  subscription: { dictionary, preview, loadings, errors },
  auth: { user, isAuth },
}) => ({
  dictionary,
  preview,
  errors,
  user,
  isAuth,
  dictionaryLoading: loadings.dictionary,
  registrationLoading: loadings.registration,
  paymentLoading: loadings.payment,
  previewLoading: loadings.preview,
  cards: userStore.cards,
  cardsLoading: userStore.loadings.cards,
  partner: partner.data || {},
  partnerSettings: partner.data.settings || {},
});

const mapDispatchToProps = dispatch => ({
  getSubscriptionDictionary: data =>
    dispatch(SubscriptionActions.getSubscriptionDictionary.action(data)),
  addSubscriptionRegistration: data => {
    dispatch(SubscriptionActions.addSubscriptionRegistration.action(data));
  },
  clearSubscriptionInfo: () => {
    dispatch(SubscriptionActions.clearSubscriptionInfo.action());
  },
  manageAuthDialogState: props =>
    dispatch(AuthActions.manageAuthDialogState.action({ state: true, props })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Subscription);
